import throttle from 'lodash/throttle';
import { useCallback, useMemo } from 'react';
import { MediaEventType } from 'api/beacon';
import { MEDIA_PLAYBACK_HEARTBEAT_MILLIS } from 'utils/constants';
import { useTrackingDispatch } from '../TrackingDispatchContext';
import { MediaEvent } from '../types';

export default function useMediaEventTracking() {
  const { dispatch } = useTrackingDispatch();

  const createMediaEventTracker = useCallback(
    (event: MediaEventType) => {
      return (payload: MediaEvent['payload']) => {
        dispatch({ event, payload });
      };
    },
    [dispatch],
  );

  const trackMediaPlaybackStarted = useMemo(
    () => createMediaEventTracker('mediaPlaybackStarted'),
    [createMediaEventTracker],
  );

  const trackMediaPlaybackHeartbeat = useMemo(
    () =>
      throttle(
        createMediaEventTracker('mediaPlaybackHeartbeat'),
        MEDIA_PLAYBACK_HEARTBEAT_MILLIS,
        { trailing: false },
      ),
    [createMediaEventTracker],
  );

  const trackMediaPaused = useMemo(
    () => createMediaEventTracker('mediaPaused'),
    [createMediaEventTracker],
  );

  const trackMediaCompleted = useMemo(
    () => createMediaEventTracker('mediaCompleted'),
    [createMediaEventTracker],
  );

  const trackMediaSeekStarted = useMemo(
    () => createMediaEventTracker('mediaSeekStarted'),
    [createMediaEventTracker],
  );

  const trackMediaSeekCompleted = useMemo(
    () => createMediaEventTracker('mediaSeekCompleted'),
    [createMediaEventTracker],
  );

  const trackMediaBufferStarted = useMemo(
    () => createMediaEventTracker('mediaBufferStarted'),
    [createMediaEventTracker],
  );

  const trackMediaBufferCompleted = useMemo(
    () => createMediaEventTracker('mediaBufferCompleted'),
    [createMediaEventTracker],
  );

  return useMemo(
    () => ({
      trackMediaPlaybackStarted,
      trackMediaPlaybackHeartbeat,
      trackMediaPaused,
      trackMediaCompleted,
      trackMediaSeekStarted,
      trackMediaSeekCompleted,
      trackMediaBufferStarted,
      trackMediaBufferCompleted,
    }),
    [
      trackMediaBufferCompleted,
      trackMediaBufferStarted,
      trackMediaCompleted,
      trackMediaPaused,
      trackMediaPlaybackHeartbeat,
      trackMediaPlaybackStarted,
      trackMediaSeekCompleted,
      trackMediaSeekStarted,
    ],
  );
}
